import { DedicatedHostRecommendation, DedicatedHostServer, ServerMatch } from '@amzn/awscat-portfolio-assessment-types';
import { SpaceBetween } from '@amzn/awsui-components-react';
import { useContext } from 'react';

import { constants } from '../../../../common/Localization';
import { awsRightSizePolicyIdToString } from '../../../../common/RightSizingPolicyName';
import AWSCATTable from '../../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../../common/table/types';
import { TcoContext } from '../../../../contexts/TcoContext';

type Props = {
    dedicatedHostRecommendations: DedicatedHostRecommendation[],
    dedicatedHostServers: DedicatedHostServer[],
};

function DedicatedHosts({ dedicatedHostRecommendations, dedicatedHostServers }: Props ): JSX.Element {

    const { assessment } = useContext(TcoContext);

    const getInstanceTypeCount = (serverMatches: ServerMatch[], instanceType: string): number => {
        return serverMatches.filter(serverMatch => serverMatch.InstanceType.split('.')[1] === instanceType).length;
    };

    const hostRecommendationsItems = (dedicatedHostRecommendations).map(host => {
        return {
            ...host,
            instanceFamily: host.dedicatedHostInstanceFamily,
            nano: getInstanceTypeCount(host.serverMatches, 'nano'),
            micro: getInstanceTypeCount(host.serverMatches, 'micro'),
            small: getInstanceTypeCount(host.serverMatches, 'small'),
            medium: getInstanceTypeCount(host.serverMatches, 'medium'),
            large: getInstanceTypeCount(host.serverMatches, 'large'),
            xlarge: getInstanceTypeCount(host.serverMatches, 'xlarge'),
            '2xlarge': getInstanceTypeCount(host.serverMatches, '2xlarge'),
            '3xlarge': getInstanceTypeCount(host.serverMatches, '3xlarge'),
            '4xlarge': getInstanceTypeCount(host.serverMatches, '4xlarge'),
            '6xlarge': getInstanceTypeCount(host.serverMatches, '6xlarge'),
            '8xlarge': getInstanceTypeCount(host.serverMatches, '8xlarge'),
            '9xlarge': getInstanceTypeCount(host.serverMatches, '9xlarge'),
            '10xlarge': getInstanceTypeCount(host.serverMatches, '10xlarge'),
            '12xlarge': getInstanceTypeCount(host.serverMatches, '12xlarge'),
            '16xlarge': getInstanceTypeCount(host.serverMatches, '16xlarge'),
            '18xlarge': getInstanceTypeCount(host.serverMatches, '18xlarge'),
            '24xlarge': getInstanceTypeCount(host.serverMatches, '24xlarge'),
            '32xlarge': getInstanceTypeCount(host.serverMatches, '32xlarge'),
            metal: getInstanceTypeCount(host.serverMatches, 'metal'),
        };
    });

    type HostRecommendationType = {
        instanceFamily: string;
        nano: number;
        micro: number;
        small: number;
        medium: number;
        large: number;
        xlarge: number;
        '2xlarge': number;
        '3xlarge': number;
        '4xlarge': number;
        '6xlarge': number;
        '8xlarge': number;
        '9xlarge': number;
        '10xlarge': number;
        '12xlarge': number;
        '16xlarge': number;
        '18xlarge': number;
        '24xlarge': number;
        '32xlarge': number;
        metal: number;
    } & DedicatedHostRecommendation;

    const columnDefinition: ColumnDefinition<HostRecommendationType>[] = [
        { header: constants.HOST_NAME, type: ColumnTypes.TEXT, id: 'hostName', cell: item => item.hostName },
        { header: constants.PHYSICAL_CORES, type: ColumnTypes.TEXT, id: 'physicalCores', cell: item => item.physicalCores },
        { header: constants.V_CPU, type: ColumnTypes.TEXT, id: 'vcpuOnUI', cell: item => item.vcpuOnUI },
        { header: constants.INSTANCE_FAMILY, type: ColumnTypes.TEXT, id: 'instanceFamily', cell: item => item.instanceFamily },
        { header: constants.UNALLOCATD_VCORES, type: ColumnTypes.TEXT, id: 'unallocatedVirtualCores', cell: item => item.unallocatedVirtualCores },
        { header: constants.NANO, type: ColumnTypes.TEXT, id: 'nano', data: item => item.nano ?? '-', cell: item => item.nano ?? '-' },
        { header: constants.MICRO, type: ColumnTypes.TEXT, id: 'micro', data: item => item.micro ?? '-', cell: item => item.micro ?? '-' },
        { header: constants.SMALL, type: ColumnTypes.TEXT, id: 'small', data: item => item.small ?? '-', cell: item => item.small ?? '-' },
        { header: constants.MEDIUM, type: ColumnTypes.TEXT, id: 'medium', data: item => item.medium ?? '-', cell: item => item.medium ?? '-' },
        { header: constants.LARGE, type: ColumnTypes.TEXT, id: 'large', data: item => item.large ?? '-', cell: item => item.large ?? '-' },
        { header: constants.X_LARGE, type: ColumnTypes.TEXT, id: 'xlarge', data: item => item.xlarge ?? '-', cell: item => item.xlarge ?? '-' },
        { header: constants.X2_LARGE, type: ColumnTypes.TEXT, id: '2xlarge', data: item => item['2xlarge'] ?? '-', cell: item => item['2xlarge'] ?? '-' },
        { header: constants.X3_LARGE, type: ColumnTypes.TEXT, id: '3xlarge', data: item => item['3xlarge'] ?? '-', cell: item => item['3xlarge'] ?? '-' },
        { header: constants.X4_LARGE, type: ColumnTypes.TEXT, id: '4xlarge', data: item => item['4xlarge'] ?? '-', cell: item => item['4xlarge'] ?? '-' },
        { header: constants.X6_LARGE, type: ColumnTypes.TEXT, id: '6xlarge', data: item => item['6xlarge'] ?? '-', cell: item => item['6xlarge'] ?? '-' },
        { header: constants.X8_LARGE, type: ColumnTypes.TEXT, id: '8xlarge', data: item => item['8xlarge'] ?? '-', cell: item => item['8xlarge'] ?? '-' },
        { header: constants.X9_LARGE, type: ColumnTypes.TEXT, id: '9xlarge', data: item => item['9xlarge'] ?? '-', cell: item => item['9xlarge'] ?? '-' },
        { header: constants.X10_LARGE, type: ColumnTypes.TEXT, id: '10xlarge', data: item => item['10xlarge'] ?? '-', cell: item => item['10xlarge'] ?? '-' },
        { header: constants.X12_LARGE, type: ColumnTypes.TEXT, id: '12xlarge', data: item => item['12xlarge'] ?? '-', cell: item => item['12xlarge'] ?? '-' },
        { header: constants.X16_LARGE, type: ColumnTypes.TEXT, id: '16xlarge', data: item => item['16xlarge'] ?? '-', cell: item => item['16xlarge'] ?? '-' },
        { header: constants.X18_LARGE, type: ColumnTypes.TEXT, id: '18xlarge', data: item => item['18xlarge'] ?? '-', cell: item => item['18xlarge'] ?? '-' },
        { header: constants.X24_LARGE, type: ColumnTypes.TEXT, id: '24xlarge', data: item => item['24xlarge'] ?? '-', cell: item => item['24xlarge'] ?? '-' },
        { header: constants.X32_LARGE, type: ColumnTypes.TEXT, id: '32xlarge', data: item => item['32xlarge'] ?? '-', cell: item => item['32xlarge'] ?? '-' },
        { header: constants.METAL, type: ColumnTypes.TEXT, id: 'metal', data: item => item.metal ?? '-', cell: item => item.metal ?? '-' },
    ];

    const colWithText = columnDefinition.map(col => (
        {
            ...col,
            text: col.header?.toString(),
        }
    ));

    type DedicatedHostServerItem = {
        serverId: string;
        hostName: string;
        instanceType: string;
    };

    const dedicatedHostServersItems: DedicatedHostServerItem[] = (dedicatedHostServers).map(dhServer => ({
        serverId: dhServer.serverId,
        hostName: dhServer.hostName || '',
        instanceType: dhServer.instanceType,
    }));

    const dedicatedHostServerColumnDefinition: ColumnDefinition<DedicatedHostServerItem>[] = [
        { header: constants.SERVER_ID, type: ColumnTypes.TEXT, id: 'serverId', cell: item => item.serverId },
        { header: constants.HOST_NAME, type: ColumnTypes.TEXT, id: 'hostName', cell: item => item.hostName },
        { header: constants.INSTANCE_TYPE, type: ColumnTypes.TEXT, id: 'instanceType', cell: item => item.instanceType },
    ];
    const dedicatedHostServerColWithText = dedicatedHostServerColumnDefinition.map(col => (
        {
            ...col,
            text: col.header?.toString(),
        }
    ));

    return (
        <SpaceBetween size='l'>
            <AWSCATTable
                resourceName={`${constants.AWS_DEDICATED_HOST_RECOMMENDATIONS} - 
                    ${awsRightSizePolicyIdToString(assessment?.configuration.awsEC2Pricing.awsRightSizePolicy ?? '')}`}
                columnDefinitions={colWithText}
                items={hostRecommendationsItems}
                autoGenerateVisibleContent
                enableAutoSorting
                resizeable
                useDefaultDownload
            />
            <AWSCATTable
                resourceName='Details'
                columnDefinitions={dedicatedHostServerColWithText}
                items={dedicatedHostServersItems}
                autoGenerateVisibleContent
                enableAutoSorting
                resizeable
                useDefaultDownload
            />
        </SpaceBetween>
    );

}

export default DedicatedHosts;
